import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AccountType } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import {
  StatusTabFilters,
  StyledTabFilter,
} from '~components/StatusTabFilters/StatusTabFilters';
import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import useRutter from '~hooks/useRutter/useRutter';
import ManagedByOwnerAlert from '~pages/Settings/Integrations/ManagedByOwnerAlert';
import QuickBooksAdvancedConfiguration from '~pages/Settings/Integrations/QuickBooksAdvancedConfiguration';
import QuickBooksDisconnectSection from '~pages/Settings/Integrations/QuickBooksDisconnectSection';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AccountMappingTab from './AccountMappingTab';
import DiscardUnsavedChangesDialog from './DiscardUnsavedChangesDialog';
import ItemsSection from './ItemsSection';
import LedgerTab from './LedgerTab';
import PaymentTermsTab from './PaymentTermsTab';
import {
  QuickBooksMappingQueryProvider,
  useQuickBooksMappingQueries,
} from './QuickBooksMappingQueryContext';
import TaxRatesTab from './TaxRatesTab';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online';

enum QuickBooksMappingTab {
  Customers = 'customers',
  Vendors = 'vendors',
  Items = 'items',
  Ledger = 'ledger',
  TaxRates = 'tax_rates',
  PaymentTerms = 'payment_terms',
  // reached through the "More" menu rather than a tab of its own; shows settings, not a grid
  Advanced = 'advanced',
}

const TAB_FILTERS = [
  {
    label: t(`${I18N_PREFIX}.tread_first.tabs.customers`),
    value: QuickBooksMappingTab.Customers,
  },
  {
    label: t(`${I18N_PREFIX}.tabs.vendors`),
    value: QuickBooksMappingTab.Vendors,
  },
  {
    label: t(`${I18N_PREFIX}.tabs.items`),
    value: QuickBooksMappingTab.Items,
  },
  {
    label: t(`${I18N_PREFIX}.tabs.ledger`),
    value: QuickBooksMappingTab.Ledger,
  },
  {
    label: t(`${I18N_PREFIX}.tread_first.tabs.tax_rates`),
    value: QuickBooksMappingTab.TaxRates,
  },
  {
    label: t(`${I18N_PREFIX}.tread_first.tabs.payment_terms`),
    value: QuickBooksMappingTab.PaymentTerms,
  },
];

const TAB_BANNERS: Partial<Record<QuickBooksMappingTab, string>> = {
  [QuickBooksMappingTab.Customers]: `${I18N_PREFIX}.tread_first.banners.customers`,
  [QuickBooksMappingTab.Vendors]: `${I18N_PREFIX}.tread_first.banners.vendors`,
  [QuickBooksMappingTab.Ledger]: `${I18N_PREFIX}.tread_first.banners.ledger`,
  [QuickBooksMappingTab.TaxRates]: `${I18N_PREFIX}.tread_first.banners.tax_rates`,
  [QuickBooksMappingTab.PaymentTerms]: `${I18N_PREFIX}.tread_first.banners.payment_terms`,
};

function QuickBooksMappingPageContent() {
  const theme = useTheme();
  const { connection, isError, isManagedByOwner } = useQuickBooksMappingQueries();
  const { enqueueImport } = useRutter();
  const { toasterStore, userStore } = useStores();

  const moreMenuRef = useRef<MenuHandler>(null);
  const [selectedTab, setSelectedTab] = useState<QuickBooksMappingTab>(
    QuickBooksMappingTab.Customers,
  );
  const [isEnqueuingImport, setIsEnqueuingImport] = useState(false);
  const [hasUnsavedItemRules, setHasUnsavedItemRules] = useState(false);
  const [pendingTab, setPendingTab] = useState<QuickBooksMappingTab | null>(null);
  const isAdvancedSelected = selectedTab === QuickBooksMappingTab.Advanced;

  // The BE rejects settings writes for the Tread super-company (403), so the export
  // settings entry point is hidden there, mirroring Settings → Configuration. The target is
  // the company shown on this page, which the company selector may have switched.
  const canEditExportSettings =
    userStore.getPermissions().canEditCompany &&
    getEffectiveUserCompanyId(userStore) !== TREAD_UUID;

  const requestTabChange = (nextTab: QuickBooksMappingTab) => {
    if (nextTab !== selectedTab && hasUnsavedItemRules) {
      setPendingTab(nextTab);

      return;
    }

    setSelectedTab(nextTab);
  };

  const confirmTabChange = () => {
    if (pendingTab) {
      setHasUnsavedItemRules(false);
      setSelectedTab(pendingTab);
    }

    setPendingTab(null);
  };

  const handleSelectAdvanced = () => {
    requestTabChange(QuickBooksMappingTab.Advanced);
    moreMenuRef.current?.onClose?.();
  };

  const handleRefreshAllData = async () => {
    if (!connection?.id) {
      return;
    }

    setIsEnqueuingImport(true);

    try {
      // the endpoint only enqueues the import; completion feedback is a follow-up
      await enqueueImport(connection.id);
      toasterStore.push(
        alert(t(`${I18N_PREFIX}.data_sync_in_progress`), AlertTypes.success),
      );
    } catch {
      // enqueueImport already surfaced the failure (toast + Sentry); swallow it so
      // the click handler does not reject
    } finally {
      setIsEnqueuingImport(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '1000px' }}>
      <Button
        color="secondary"
        component={Link}
        startIcon={<ArrowBackIosNew fontSize="small" />}
        to={`/${routes.settings.base}/${routes.settings.integrations.base}/${routes.settings.integrations.rutter.base}`}
        variant="text"
      >
        {t(`${I18N_PREFIX}.back_button`)}
      </Button>

      <CardContent
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: '30px',
          paddingX: 0,
        }}
      >
        <Typography variant="h4">{t(`${I18N_PREFIX}.tread_first.title`)}</Typography>

        <Box sx={{ alignItems: 'center', display: 'flex' }}>
          <Typography
            color={theme.brandV2.colors.treadGray3}
            sx={{ mr: '6px' }}
            variant="body1"
          >
            {t(`${I18N_PREFIX}.last_sync`, {
              time: connection?.synchronizedAt
                ? dayjs.tz(connection.synchronizedAt).format("h:mm A ddd MMM-DD 'YY")
                : '',
              interpolation: { escapeValue: false },
            })}
          </Typography>
          {!isManagedByOwner && (
            <SmallButton
              color="brandV2Yellow"
              disabled={isEnqueuingImport || !connection?.id}
              onClick={handleRefreshAllData}
            >
              {t(`${I18N_PREFIX}.refresh_button`)}
            </SmallButton>
          )}
        </Box>
      </CardContent>

      <Box sx={{ alignItems: 'center', display: 'flex', mb: '24px' }}>
        <StatusTabFilters
          filters={TAB_FILTERS}
          onChange={requestTabChange}
          value={selectedTab}
        />

        {canEditExportSettings && (
          <>
            <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
            <Menu
              menuTrigger={
                <StyledTabFilter
                  data-test-id="quick-books-more-menu"
                  selected={isAdvancedSelected}
                  value={QuickBooksMappingTab.Advanced}
                >
                  {t(`${I18N_PREFIX}.advanced_configuration.more`)}
                  <ArrowDropDown fontSize="small" sx={{ ml: 0.5 }} />
                </StyledTabFilter>
              }
              ref={moreMenuRef}
            >
              <Typography
                sx={{ color: 'text.secondary', display: 'block', px: 1.5, pt: 1 }}
                variant="overline"
              >
                {t(`${I18N_PREFIX}.advanced_configuration.menu_section`)}
              </Typography>
              <MenuItem onClick={handleSelectAdvanced} selected={isAdvancedSelected}>
                <ListItemText
                  primary={t(`${I18N_PREFIX}.advanced_configuration.menu_item`)}
                />
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {connection && isManagedByOwner && <ManagedByOwnerAlert connection={connection} />}

      {isAdvancedSelected ? (
        <>
          <QuickBooksAdvancedConfiguration connection={connection} />
          {connection && !isManagedByOwner && (
            <QuickBooksDisconnectSection connection={connection} />
          )}
        </>
      ) : (
        <>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }} variant="outlined">
              {t('error_messages.rutter.failed_to_load_mapping_data')}
            </Alert>
          )}

          {selectedTab !== QuickBooksMappingTab.Items && (
            <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
              {t(TAB_BANNERS[selectedTab] ?? '')}
            </Alert>
          )}

          {selectedTab === QuickBooksMappingTab.Customers && (
            <AccountMappingTab accountType={AccountType.CUSTOMER} />
          )}
          {selectedTab === QuickBooksMappingTab.Vendors && (
            <AccountMappingTab accountType={AccountType.VENDOR} />
          )}
          {selectedTab === QuickBooksMappingTab.Items && (
            <ItemsSection onItemRulesDirtyChange={setHasUnsavedItemRules} />
          )}
          {selectedTab === QuickBooksMappingTab.Ledger && <LedgerTab />}
          {selectedTab === QuickBooksMappingTab.TaxRates && <TaxRatesTab />}
          {selectedTab === QuickBooksMappingTab.PaymentTerms && <PaymentTermsTab />}
        </>
      )}

      <DiscardUnsavedChangesDialog
        isOpen={pendingTab !== null}
        onClose={() => setPendingTab(null)}
        onConfirm={confirmTabChange}
      />
    </Box>
  );
}

export default function QuickBooksMappingPage() {
  return (
    <QuickBooksMappingQueryProvider>
      <QuickBooksMappingPageContent />
    </QuickBooksMappingQueryProvider>
  );
}
